import { useState, useEffect, useCallback } from "react";

// ─── Config ────────────────────────────────────────────────────────────────────
// These values come from your .env.local file in the Next.js backend
const API_BASE = "https://portal.powervisionbi.com/api";
const ENTRA_CLIENT_ID = "db805003-64da-4cc7-80eb-8e852d8e651f";
const ENTRA_TENANT_DOMAIN = "pvbicustomers.ciamlogin.com";
const ENTRA_REDIRECT_URI = "https://portal.powervisionbi.com";
// ─── MSAL (Microsoft Authentication Library) ──────────────────────────────────
// Load MSAL from CDN — add this to your index.html:
// <script src="https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js"></script>
let _msalInstance = null;
function getMsalInstance() {
  if (!window.msal) return null;
  if (_msalInstance) return _msalInstance;
  _msalInstance = new window.msal.PublicClientApplication({
    auth: {
      clientId: ENTRA_CLIENT_ID,
      authority: `https://${ENTRA_TENANT_DOMAIN}`,
      redirectUri: ENTRA_REDIRECT_URI,
      knownAuthorities: [ENTRA_TENANT_DOMAIN],
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  return _msalInstance;
}

// ─── Auth helpers ──────────────────────────────────────────────────────────────
async function signInWithEntra() {
  const msal = getMsalInstance();
  if (!msal) throw new Error("MSAL not loaded");
  await msal.initialize();
  await msal.loginRedirect({
    scopes: ["openid", "profile", "email"],
  });
}

async function getAccessToken() {
  const msal = getMsalInstance();
  if (!msal) return null;
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (!accounts.length) return null;
  try {
    const result = await msal.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account: accounts[0],
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

async function signOut() {
  const msal = getMsalInstance();
  if (!msal) return;
  await msal.initialize();
  await msal.logoutPopup();
}

// ─── API calls ─────────────────────────────────────────────────────────────────
async function fetchReports(token) {
  const t = token || sessionStorage.getItem("pvbi_token");
  const res = await fetch(`${API_BASE}/reports`, {
    headers: { Authorization: `Bearer ${t}` },
  });
  if (!res.ok) throw new Error("Failed to load reports");
  return res.json();
}

async function fetchEmbedToken(token, reportId) {
  const res = await fetch(`${API_BASE}/embed-token`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ reportId }),
  });
  if (!res.ok) throw new Error("Failed to get embed token");
  return res.json(); // { token, embedUrl, expiry, reportName }
}

// ─── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg: "#0d1117", surface: "#161b22", surfaceHover: "#1f2937",
  border: "#21262d", borderHover: "#30363d",
  accent: "#c47a10", accentDark: "#a86209", accentLight: "#e8950f",
  accentGlow: "rgba(196,122,16,0.15)", accentGlow2: "rgba(196,122,16,0.07)",
  success: "#2ea043", successGlow: "rgba(46,160,67,0.15)",
  error: "#f85149", errorGlow: "rgba(248,81,73,0.12)",
  warn: "#d29922", textPrimary: "#e6edf3",
  textSecondary: "#8b949e", textMuted: "#484f58",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Plus Jakarta Sans',sans-serif;background:${T.bg};color:${T.textPrimary};min-height:100vh;overflow-x:hidden}
  .mono{font-family:'JetBrains Mono',monospace}
  .grid-bg{position:fixed;inset:0;pointer-events:none;z-index:0;
    background-image:linear-gradient(rgba(196,122,16,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(196,122,16,0.035) 1px,transparent 1px);
    background-size:48px 48px}
  .orb{position:fixed;border-radius:50%;pointer-events:none;z-index:0;filter:blur(90px)}
  @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes pulse2{0%,100%{opacity:1}50%{opacity:.45}}
  .fu{animation:fadeUp .45s ease both}
  .fu1{animation:fadeUp .45s .06s ease both}
  .fu2{animation:fadeUp .45s .13s ease both}
  .fu3{animation:fadeUp .45s .2s ease both}
  .spin{animation:spin .7s linear infinite}
  .field-group{display:flex;flex-direction:column;gap:6px}
  .field-label{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${T.textSecondary}}
  .field-input{background:rgba(255,255,255,.04);border:1px solid ${T.border};border-radius:8px;color:${T.textPrimary};font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;padding:11px 14px;outline:none;transition:border-color .2s,box-shadow .2s,background .2s;width:100%}
  .field-input:focus{border-color:${T.accent};box-shadow:0 0 0 3px ${T.accentGlow};background:rgba(196,122,16,.04)}
  .field-input::placeholder{color:${T.textMuted}}
  .btn-primary{background:${T.accent};border:none;border-radius:8px;color:#fff;cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:600;padding:12px 24px;transition:background .2s,transform .1s,box-shadow .2s;width:100%}
  .btn-primary:hover{background:${T.accentLight};box-shadow:0 0 28px ${T.accentGlow};transform:translateY(-1px)}
  .btn-primary:active{transform:translateY(0)}
  .btn-primary:disabled{opacity:.45;cursor:not-allowed;transform:none}
  .btn-secondary{background:transparent;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:9px 18px;transition:border-color .2s,color .2s,background .2s}
  .btn-secondary:hover{border-color:${T.borderHover};color:${T.textPrimary};background:rgba(255,255,255,.04)}
  .btn-ghost{background:none;border:none;color:${T.accent};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:4px 0;transition:opacity .2s}
  .btn-ghost:hover{opacity:.72}
  .card{background:${T.surface};border:1px solid ${T.border};border-radius:12px;padding:24px}
  .divider{border:none;border-top:1px solid ${T.border};margin:20px 0}
  .spinner{width:17px;height:17px;border:2px solid rgba(255,255,255,.2);border-top-color:#fff;border-radius:50%;display:inline-block}
  .alert{border-radius:8px;padding:12px 16px;font-size:13px;font-weight:500;display:flex;align-items:flex-start;gap:10px;animation:fadeUp .3s ease}
  .alert-error{background:${T.errorGlow};border:1px solid rgba(248,81,73,.25);color:#fca5a5}
  .alert-success{background:${T.successGlow};border:1px solid rgba(46,160,67,.25);color:#86efac}
  .alert-info{background:${T.accentGlow2};border:1px solid rgba(196,122,16,.2);color:#fcd34d}
  .badge{display:inline-flex;align-items:center;gap:5px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:.04em;padding:3px 10px}
  .badge-success{background:${T.successGlow};color:${T.success};border:1px solid rgba(46,160,67,.25)}
  .badge-info{background:${T.accentGlow};color:${T.accent};border:1px solid rgba(196,122,16,.25)}
  .nav-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:background .15s,color .15s;color:${T.textSecondary};border:none;background:none;width:100%;text-align:left;font-family:'Plus Jakarta Sans',sans-serif}
  .nav-item:hover{background:rgba(255,255,255,.05);color:${T.textPrimary}}
  .nav-item.active{background:${T.accentGlow};color:${T.accent}}
  .report-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:20px;cursor:pointer;transition:border-color .2s,transform .2s,box-shadow .2s;animation:fadeUp .4s ease both}
  .report-card:hover{border-color:${T.accent};transform:translateY(-2px);box-shadow:0 8px 32px rgba(196,122,16,.12)}
  .stat-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:18px 20px;animation:fadeUp .4s ease both}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
`;

const Icon = ({ n, size = 16, color = "currentColor" }) => {
  const icons = {
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    db: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
    chart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    grid: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    logout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    microsoft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><rect x="1" y="1" width="10.5" height="10.5" fill="#f25022"/><rect x="12.5" y="1" width="10.5" height="10.5" fill="#7fba00"/><rect x="1" y="12.5" width="10.5" height="10.5" fill="#00a4ef"/><rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#ffb900"/></svg>,
  };
  return icons[n] || null;
};

const CAT_COLOR = { Finance: T.accent, Sales: T.success, Operations: "#a78bfa", Purchasing: T.warn };

// ─── Login ─────────────────────────────────────────────────────────────────────
// Now uses Microsoft Entra External ID via MSAL popup
function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handleMicrosoftSignIn = async () => {
    setErr(""); setLoading(true);
    try {
      await signInWithEntra(); // redirects away — no return value
    } catch (e) {
      setErr("Sign-in failed. Please try again or contact Power Vision BI support.");
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, position: "relative", zIndex: 1 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div className="fu" style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          <div style={{ marginTop: 10, height: 1, width: 200, background: `linear-gradient(90deg, transparent, ${T.accent}, transparent)` }} />
          <p style={{ marginTop: 10, fontSize: 12, color: T.textMuted, letterSpacing: "0.06em" }}>CLIENT ANALYTICS PORTAL</p>
        </div>

        <div className="card fu1" style={{ padding: 32 }}>
          <h2 style={{ fontSize: 19, fontWeight: 600, marginBottom: 4 }}>Welcome back</h2>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 28 }}>Sign in with your company account to view your reports</p>

          {err && <div className="alert alert-error" style={{ marginBottom: 18 }}><Icon n="alert" size={15} color={T.error} />{err}</div>}

          {/* Microsoft sign-in button */}
          <button
            className="btn-primary"
            onClick={handleMicrosoftSignIn}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#fff", color: "#1a1a1a", border: "1px solid #ddd" }}
          >
            {loading
              ? <><span className="spinner spin" style={{ borderTopColor: T.accent, borderColor: "rgba(196,122,16,.3)" }} />Signing in…</>
              : <><Icon n="microsoft" size={18} />Continue with Microsoft</>
            }
          </button>

          <hr className="divider" />
          <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center" }}>
            New customer? <button className="btn-ghost" style={{ fontSize: 12 }}>Contact Power Vision BI</button>
          </p>
        </div>

        <p className="fu2" style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: T.textMuted }}>
          <span style={{ color: "#ffffff", fontWeight: 600, letterSpacing: "0.04em" }}>We Make Data Dance!</span> · Secured with Microsoft Entra ID
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────────
// Now loads real reports from /api/reports and embeds via /api/embed-token
function Dashboard({ user, token, onLogout }) {
  const [nav, setNav] = useState("reports");
  const [reports, setReports] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsErr, setReportsErr] = useState("");
  const [activeReport, setActiveReport] = useState(null);
  const [embedData, setEmbedData] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [embedErr, setEmbedErr] = useState("");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
const [messages, setMessages] = useState([]);
const [expandedTenant, setExpandedTenant] = useState(null);
const [showAddUser, setShowAddUser] = useState(false);
const [addUserTenantId, setAddUserTenantId] = useState("");
const [addUserTenantName, setAddUserTenantName] = useState("");
const [newUser, setNewUser] = useState({ name: "", email: "", role: "VIEWER" });
const [inviteLoading, setInviteLoading] = useState(false);
const [adminData, setAdminData] = useState(null);
const [showAddTenant, setShowAddTenant] = useState(false);
const inviteUser = async () => {
  if (!newUser.name || !newUser.email) return;
  setInviteLoading(true);
  try {
    const res = await fetch(`${API_BASE}/admin/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId: addUserTenantId, ...newUser }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setShowAddUser(false);
    setNewUser({ name: "", email: "", role: "VIEWER" });
    loadAdminData();
    alert(`✅ Invitation sent to ${newUser.email}!`);
  } catch (err) {
    alert(`❌ Error: ${err.message}`);
  } finally {
    setInviteLoading(false);
  }
};
const [newTenant, setNewTenant] = useState({ 
  name: "", 
  slug: "", 
  pbiWorkspaceId: "",
  dbType: "hosted",
  dbConnectionString: "",
  dbSchema: "public"
});
const [input, setInput] = useState("");
const [aiLoading, setAiLoading] = useState(false);
const loadAdminData = async () => {
  try {
    const res = await fetch(`${API_BASE}/admin/tenants`, {
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}` }
    });
    const data = await res.json();
    setAdminData(data);
  } catch (err) {
    console.error("Admin data error:", err);
  }
};

const addTenant = async () => {
  try {
    await fetch(`${API_BASE}/admin/tenants`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify(newTenant)
    });
    setShowAddTenant(false);
    setNewTenant({ name: "", slug: "", pbiWorkspaceId: "", dbType: "hosted", dbConnectionString: "", dbSchema: "public" });
    loadAdminData();
  } catch (err) {
    console.error("Add tenant error:", err);
  }
};
const sendMessage = async () => {
  if (!input.trim() || aiLoading) return;
  const question = input.trim();
  setInput("");
  setMessages(prev => [...prev, { role: "user", content: question }]);
  setAiLoading(true);
  try {
    const res = await fetch(`${API_BASE}/ai-query`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify({ question }),
    });
    const data = await res.json();
    setMessages(prev => [...prev, { role: "assistant", content: data.answer || "Sorry, I could not answer that." }]);
  } catch {
    setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
  } finally {
    setAiLoading(false);
  }
};
 useEffect(() => {
  let cancelled = false;
  async function load() {
    setLoadingReports(true); setReportsErr("");
    try {
      const data = await fetchReports(token);
      if (!cancelled) {
        setReports(data.reports || []);
        setTenantName(data.tenant?.name || "");
      }
    } catch (e) {
      if (!cancelled) setReportsErr("Failed to load reports. Please refresh or contact support.");
    } finally {
      if (!cancelled) setLoadingReports(false);
    }
    // Load admin data if admin user
    if (user.email === "christiaan.horn@powervisionbi.com") {
      loadAdminData();
    }
  }
  load();
  return () => { cancelled = true; };
}, [token]);

  // Open a report — fetch embed token from backend
  const openReport = useCallback(async (report) => {
    setActiveReport(report);
    setEmbedData(null);
    setEmbedErr("");
    setLoadingEmbed(true);
    try {
      const data = await fetchEmbedToken(token, report.id);
      setEmbedData(data);
    } catch (e) {
      setEmbedErr("Could not load this report. Please try again.");
    } finally {
      setLoadingEmbed(false);
    }
  }, [token]);

  const cats = ["All", ...Array.from(new Set(reports.map(r => r.cat || "Other")))];
  const filtered = reports.filter(r => {
    const ms = r.name.toLowerCase().includes(search.toLowerCase()) || (r.description || "").toLowerCase().includes(search.toLowerCase());
    return ms && (cat === "All" || r.cat === cat);
  });
  const initials = (user.name || "U").slice(0, 2).toUpperCase();

  return (
    <div style={{ display: "flex", minHeight: "100vh", position: "relative", zIndex: 1 }}>
      {/* Sidebar */}
      <div style={{ width: 224, background: T.surface, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", padding: "18px 12px", flexShrink: 0 }}>
        <div style={{ padding: "4px 8px 8px 8px", marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          {tenantName && <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{tenantName}</p>}
        </div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${T.accent}60, transparent)`, marginBottom: 20, marginLeft: 8, marginRight: 8 }} />

        <nav style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          {[
{ id: "reports", icon: "grid", label: "Reports" },
{ id: "ai", icon: "zap", label: "AI Agent" },
{ id: "settings", icon: "settings", label: "Settings" },
...(user.email === "christiaan.horn@powervisionbi.com" ? [{ id: "admin", icon: "shield", label: "Admin" }] : []),
].map(item => (
            <button key={item.id} className={`nav-item ${nav === item.id ? "active" : ""}`} onClick={() => { setNav(item.id); setActiveReport(null); }}>
              <Icon n={item.icon} size={15} />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 8px", borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</p>
            <p style={{ fontSize: 10, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</p>
          </div>
          <button onClick={onLogout} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex", padding: 4 }} title="Sign out">
            <Icon n="logout" size={14} />
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ── Reports list ── */}
        {nav === "reports" && !activeReport && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Your reports</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Live Power BI reports — click any report to open it</p>
            </div>

            {/* Stats */}
            <div className="fu1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 13, marginBottom: 28 }}>
              {[
                { label: "Reports available", value: loadingReports ? "…" : reports.length, icon: "grid", color: T.accent },
                { label: "Tenant", value: tenantName || "Loading…", icon: "db", color: T.success },
                { label: "Session status", value: "Active", icon: "refresh", color: T.warn },
              ].map((s, i) => (
                <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</p>
                      <p style={{ fontSize: 20, fontWeight: 600, color: s.color }}>{s.value}</p>
                    </div>
                    <div style={{ color: s.color, opacity: 0.7 }}><Icon n={s.icon} size={18} /></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error state */}
            {reportsErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{reportsErr}</div>}

            {/* Loading state */}
            {loadingReports && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 32, color: T.textSecondary }}>
                <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                Loading your reports…
              </div>
            )}

            {!loadingReports && !reportsErr && (
              <>
                {/* Filters */}
                <div className="fu2" style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
                  <input className="field-input" style={{ maxWidth: 240, padding: "9px 14px" }} placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
                  <div style={{ display: "flex", gap: 6 }}>
                    {cats.map(c => (
                      <button key={c} onClick={() => setCat(c)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${cat === c ? T.accent : T.border}`, background: cat === c ? T.accentGlow : "transparent", color: cat === c ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "all .15s" }}>{c}</button>
                    ))}
                  </div>
                </div>

                {/* Report grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                  {filtered.map((r, i) => (
                    <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => openReport(r)}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                        {r.cat && <span className="badge" style={{ background: `${CAT_COLOR[r.cat] || T.accent}18`, color: CAT_COLOR[r.cat] || T.accent, border: `1px solid ${CAT_COLOR[r.cat] || T.accent}30`, fontSize: 10 }}>{r.cat}</span>}
                        {r.updated && <span style={{ fontSize: 11, color: T.textMuted }}>{r.updated}</span>}
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
                      <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
                      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
                        <Icon n="external" size={13} /> Open report
                      </div>
                    </div>
                  ))}
                  {filtered.length === 0 && !loadingReports && (
                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 48, color: T.textMuted }}>
                      <Icon n="chart" size={32} color={T.border} />
                      <p style={{ marginTop: 12, fontSize: 14 }}>No reports found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Power BI Report Viewer ── */}
        {nav === "reports" && activeReport && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setActiveReport(null); setEmbedData(null); }}>← Back</button>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>{activeReport.name}</h2>
                <p style={{ fontSize: 12, color: T.textSecondary }}>{activeReport.description}</p>
              </div>
              <span className="badge badge-success"><span style={{ width: 5, height: 5, borderRadius: "50%", background: T.success }} />Live data</span>
            </div>

            <div style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 500 }}>

              {loadingEmbed && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: T.textSecondary }}>
                  <span className="spinner spin" style={{ width: 28, height: 28, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                  <p style={{ fontSize: 14 }}>Loading report…</p>
                </div>
              )}

              {embedErr && (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <div className="alert alert-error" style={{ display: "inline-flex" }}><Icon n="alert" size={15} color={T.error} />{embedErr}</div>
                  <br /><br />
                  <button className="btn-secondary" onClick={() => openReport(activeReport)}>Retry</button>
                </div>
              )}

              {/* Power BI Embedded iframe — uses the token from /api/embed-token */}
             {embedData && !loadingEmbed && (
  <div
    ref={el => {
      if (el && embedData && window.powerbi) {
        window.powerbi.reset(el);
        window.powerbi.embed(el, {
          type: 'report',
          id: embedData.reportId,
          embedUrl: embedData.embedUrl,
          accessToken: embedData.token,
          tokenType: window.models?.TokenType?.Embed ?? 1,
          settings: {
            navContentPaneEnabled: true,
            filterPaneEnabled: true,
          }
        });
      }
    }}
    style={{ width: "100%", height: "100%", minHeight: 600 }}
  />
)}
            </div>
          </div>
        )}
{/* ── AI Agent ── */}
{nav === "ai" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div className="fu" style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>AI Data Agent</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Ask any question about your business data in plain English</p>
    </div>

    <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12 }} id="chat-messages">
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: T.textMuted }}>
            <Icon n="zap" size={32} color={T.border} />
            <p style={{ marginTop: 12, fontSize: 14 }}>Ask me anything about your business data</p>
            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
              {["What were my top 5 customers last month?", "Show me overdue invoices", "How is my revenue trending?"].map(q => (
                <button key={q} onClick={() => setInput(q)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 8, padding: "8px 16px", color: T.accent, cursor: "pointer", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "75%", padding: "12px 16px", borderRadius: 12, background: m.role === "user" ? T.accent : T.surface, border: `1px solid ${m.role === "user" ? T.accent : T.border}`, color: m.role === "user" ? "#fff" : T.textPrimary, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {m.content}
            </div>
          </div>
        ))}
        {aiLoading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "12px 16px", borderRadius: 12, background: T.surface, border: `1px solid ${T.border}` }}>
              <span className="spinner spin" style={{ width: 16, height: 16, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: 16, display: "flex", gap: 10 }}>
        <input
          className="field-input"
          placeholder="Ask anything about your data..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
          disabled={aiLoading}
        />
        <button className="btn-primary" style={{ width: "auto", padding: "11px 20px", whiteSpace: "nowrap" }} onClick={sendMessage} disabled={aiLoading || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  </div>
)}
{/* ── Admin Panel ── */}
{nav === "admin" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Admin Panel</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Manage clients, reports and users</p>
    </div>

    {/* Stats */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
      {[
        { label: "Total Clients", value: adminData?.tenants?.length ?? "...", icon: "users" },
        { label: "Total Reports", value: adminData?.reports?.length ?? "...", icon: "grid" },
        { label: "Platform Status", value: "Live", icon: "zap" },
      ].map(s => (
        <div key={s.label} className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Icon n={s.icon} size={16} color={T.accent} />
            <span style={{ fontSize: 12, color: T.textSecondary }}>{s.label}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
        </div>
      ))}
    </div>

{/* Clients list */}
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Clients</h2>
        <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAddTenant(true)}>
          + Add Client
        </button>
      </div>
      {adminData?.tenants?.map(t => (
        <div key={t.id} style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</p>
              <p style={{ fontSize: 12, color: T.textSecondary }}>{t.slug} · {t.dbType === "client" ? "🔗 Client DB" : "🏠 Hosted DB"}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: t.isActive ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)", color: t.isActive ? "#22c55e" : "#ef4444" }}>
                {t.isActive ? "Active" : "Inactive"}
              </span>
              <button onClick={() => setExpandedTenant(expandedTenant === t.id ? null : t.id)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", color: T.textSecondary, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {expandedTenant === t.id ? "Hide Users" : "Manage Users"}
              </button>
            </div>
          </div>

          {expandedTenant === t.id && (
            <div style={{ background: T.bg, borderRadius: 8, padding: 16, marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Users</p>
                <button onClick={() => { setShowAddUser(true); setAddUserTenantId(t.id); setAddUserTenantName(t.name); setNewUser({ name: "", email: "", role: "VIEWER" }); }} style={{ background: "none", border: `1px solid ${T.accent}`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  + Invite User
                </button>
              </div>
              {!t.users || t.users.length === 0 ? (
                <p style={{ fontSize: 12, color: T.textMuted }}>No users yet — invite your first user above.</p>
              ) : (
                t.users.map(u => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</p>
                      <p style={{ fontSize: 11, color: T.textSecondary }}>{u.email}</p>
                    </div>
                    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(196,122,16,.15)", color: T.accent }}>{u.role}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>

    {/* Invite user modal — top level so it's never trapped by overflow/transform */}
    {showAddUser && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div className="card" style={{ width: 420, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Invite User</h2>
          <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 20 }}>Adding to: {addUserTenantName}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Email address" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
            <select className="field-input" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
              <option value="VIEWER">Viewer</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={inviteUser} disabled={inviteLoading}>
              {inviteLoading ? "Sending invite..." : "Send Invitation"}
            </button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddUser(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Add tenant modal */}
    {showAddTenant && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
        <div className="card" style={{ width: 440, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Add New Client</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Company name" value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Slug (e.g. acme-corp)" value={newTenant.slug} onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value }))} />
            <input className="field-input" placeholder="Power BI Workspace ID" value={newTenant.pbiWorkspaceId} onChange={e => setNewTenant(p => ({ ...p, pbiWorkspaceId: e.target.value }))} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "hosted" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "hosted" ? T.accent : T.border}`, background: newTenant.dbType === "hosted" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "hosted" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🏠 We host (our Supabase)
              </button>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "client" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "client" ? T.accent : T.border}`, background: newTenant.dbType === "client" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "client" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🔗 Client's own DB
              </button>
            </div>
            {newTenant.dbType === "client" && (
              <>
                <input className="field-input" placeholder="Connection string (postgresql://...)" value={newTenant.dbConnectionString} onChange={e => setNewTenant(p => ({ ...p, dbConnectionString: e.target.value }))} />
                <input className="field-input" placeholder="Schema name (e.g. public)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
              </>
            )}
            {newTenant.dbType === "hosted" && (
              <input className="field-input" placeholder="Schema name (e.g. acme_corp)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={addTenant}>Create Client</button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddTenant(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Invite user modal */}
    {showAddUser && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
        <div className="card" style={{ width: 420, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Invite User</h2>
          <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 20 }}>Adding to: {addUserTenantName}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Email address" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
            <select className="field-input" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
              <option value="VIEWER">Viewer</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={inviteUser} disabled={inviteLoading}>
              {inviteLoading ? "Sending invite..." : "Send Invitation"}
            </button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddUser(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}
  </div>
)}
        {/* ── Settings ── */}
        {nav === "settings" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Settings</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Manage your account preferences</p>
            </div>
            <div className="card fu1" style={{ maxWidth: 520, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Account</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[["Display name", user.name], ["Email", user.email], ["Tenant", tenantName]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.textSecondary }}>{k}</span>
                    <span style={{ fontSize: 13, color: T.textPrimary }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card fu2" style={{ maxWidth: 520 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Session</h3>
              <button className="btn-secondary" style={{ color: T.error, borderColor: "rgba(248,81,73,.3)" }} onClick={onLogout}>Sign out</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("login");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // On mount, check if user is already signed in (MSAL session)
// On mount, check if user is already signed in (MSAL session)
useEffect(() => {
  async function checkSession() {
    const msal = getMsalInstance();
    if (!msal) return;
    await msal.initialize();
    
    // Handle redirect coming back from Microsoft
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult) {
      setUser({
        email: redirectResult.account.username,
        name: redirectResult.account.name || redirectResult.account.username.split("@")[0],
      });
      setToken(redirectResult.accessToken);
      setScreen("dashboard");
      return;
    }

    // Check if already signed in
    const accounts = msal.getAllAccounts();
    if (accounts.length) {
      try {
        const result = await msal.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: accounts[0],
        });
        setUser({ email: accounts[0].username, name: accounts[0].name || accounts[0].username.split("@")[0] });
        setToken(result.accessToken);
        sessionStorage.setItem("pvbi_token", result.accessToken);
        setScreen("dashboard");
      } catch {
        // Silent token failed, stay on login
      }
    }
  }
  checkSession();
}, []);

 const handleLogin = (u, t) => {
    setUser(u); 
    setToken(t); 
    sessionStorage.setItem("pvbi_token", t);
    setScreen("dashboard");
  };

  const handleLogout = async () => {
    await signOut();
    setUser(null); setToken(null); setScreen("login");
  };

  return (
    <>
      <style>{css}</style>
      <div className="grid-bg" />
      <div className="orb" style={{ width: 500, height: 500, top: -160, left: -120, background: "rgba(196,122,16,0.07)" }} />
      <div className="orb" style={{ width: 350, height: 350, bottom: -100, right: -80, background: "rgba(196,122,16,0.04)" }} />
      {screen === "login" && <Login onLogin={handleLogin} />}
      {screen === "dashboard" && <Dashboard user={user} token={token} onLogout={handleLogout} />}
    </>
  );
}
